import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';
import { NavItem, NavigationResponse } from './navigation.models';

export type NavStatus = 'idle' | 'loading' | 'loaded' | 'error';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private static readonly RETRY_INTERVAL_MS = 5000;
  private static readonly LOAD_TIMEOUT_MS = 60000;

  private readonly endpoint = `${environment.apiBaseUrl}/navigation`;
  private readonly skipLoaderHeaders = new HttpHeaders({ 'X-Skip-Loader': 'true' });

  private readonly _status = signal<NavStatus>('idle');
  private readonly _itemsSig = signal<NavItem[]>([]);
  private _allowedAdminRoutes = new Set<string>();

  private loadInFlight: Promise<void> | null = null;
  private resolveReady!: () => void;
  private ready: Promise<void> = new Promise((resolve) => (this.resolveReady = resolve));

  readonly status = this._status.asReadonly();

  constructor(private readonly http: HttpClient) {}

  get items(): NavItem[] {
    return this._itemsSig();
  }

  async load(): Promise<void> {
    if (this.loadInFlight) {
      return this.loadInFlight;
    }

    this.loadInFlight = this.runLoadLoop().finally(() => {
      this.loadInFlight = null;
    });
    return this.loadInFlight;
  }

  retry(): Promise<void> {
    return this.load();
  }

  whenReady(): Promise<void> {
    return this.ready;
  }

  private async runLoadLoop(): Promise<void> {
    this._status.set('loading');
    const startedAt = Date.now();

    while (true) {
      try {
        const response = await firstValueFrom(
          this.http.get<NavigationResponse>(this.endpoint, { headers: this.skipLoaderHeaders })
        );
        this._itemsSig.set(response.items);
        this._allowedAdminRoutes = this.extractAdminRoutes(response.items);
        this._status.set('loaded');
        this.resolveReady();
        return;
      } catch {
        if (Date.now() - startedAt >= NavigationService.LOAD_TIMEOUT_MS) {
          this._itemsSig.set([]);
          this._allowedAdminRoutes = new Set();
          this._status.set('error');
          this.resolveReady();
          return;
        }
        await this.delay(NavigationService.RETRY_INTERVAL_MS);
      }
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  hasAdminAccess(): boolean {
    return this._allowedAdminRoutes.size > 0;
  }

  isAdminRouteAllowed(url: string): boolean {
    const path = url.split('?')[0];
    for (const route of this._allowedAdminRoutes) {
      if (path === route || path.startsWith(route + '/')) {
        return true;
      }
    }
    return false;
  }

  private extractAdminRoutes(items: NavItem[]): Set<string> {
    const routes = new Set<string>();
    let inAdminSection = false;

    for (const item of items) {
      if (item.type === 'Header') {
        inAdminSection = item.label === 'Administração';
        continue;
      }
      if (inAdminSection) {
        this.collectRoutes(item, routes);
      }
    }

    return routes;
  }

  private collectRoutes(item: NavItem, routes: Set<string>): void {
    if (item.route) {
      routes.add(item.route);
    }
    for (const child of item.children ?? []) {
      this.collectRoutes(child, routes);
    }
  }
}
